// Journal du chantier (incrément 4) côté terrain : volet de coche, consigne de relève, temps passé, annulation motivée.
// Téléphone d'abord : chaque volet est un dialogue dont le pied (bouton « Valider ») reste visible sans défiler sur 375 × 667 et
// 360 × 740 ; le contenu défile au-dessus. Le serveur décide seul des droits et de l'affectation du jour : l'écran ne fait que
// proposer. Rien n'est inventé : le temps proposé vient de l'affectation (visible, modifiable), jamais posé en silence.
// Incrément 6 : chaque saisie passe par la file d'envoi (lib/fileEnvoi.ts) — gardée sur le téléphone d'abord, envoyée tout de suite
// s'il y a du réseau, sinon au retour du réseau ; le volet dit lequel des trois s'est produit (envoyée, gardée, refusée).
import { useMemo, useState } from 'react';
import { CloudUpload } from 'lucide-react';
import { messageErreur } from '../../../lib/api.ts';
import { useReglagesSalaries } from '../../../lib/session.tsx';
import { nombre } from '../../../lib/format.ts';
import { nouvelIdentifiant, useFileEnvoi, verdictDe, type OperationFile } from '../../../lib/fileEnvoi.ts';
import { Alerte, Bouton, Champ, ChampTexte, Dialogue, useNotifier } from '../../../components/ui.tsx';
import { quantite } from './commun.tsx';
import { ChampPhoto } from './PhotoChantier.tsx';
import { GARDEE, libelleChantier, messageSaisie, operationPhoto, texteSecondaires, useSaisie } from './saisie.ts';
import type { AffectationJour, CocheEtape, EtapeJour } from './types.ts';

const fmtHeure = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' });
const fmtJourHeure = new Intl.DateTimeFormat('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' });
/** « 10:42 » (heure de Paris). */
export const heureMinute = (v: string) => fmtHeure.format(new Date(v));
/** « mar. 14/10 10:42 » */
export const jourHeure = (v: string) => fmtJourHeure.format(new Date(v));

/** Identifiant d'envoi : rejouer le même envoi (double toucher, réseau qui hésite) ne crée aucun doublon côté serveur. */
export const nouvelEnvoi = nouvelIdentifiant;

/** Coches de cette étape gardées sur le téléphone, pas encore envoyées (affichage immédiat, mention « en attente d'envoi »). */
export const cochesEnAttente = (file: OperationFile[], etapeId: string) => file.filter((o) => o.type === 'coche' && o.etapeId === etapeId);

/** Résumé d'une coche : « Faite par Julien, avec Sarah, à 10:42 » ou « 10 m faits par … ». */
export function texteCoche(c: CocheEtape, unite: string) {
  const quoi = c.sorte === 'partiel' && c.quantite ? `${quantite(c.quantite, unite)} faits` : 'Faite';
  return `${quoi} par ${c.moi ? 'vous' : c.par}${c.avec.length ? `, avec ${c.avec.join(', ')}` : ''}, ${heureMinute(c.a)}`;
}

// ─── Volet de coche ────────────────────────────────────────────────────────────────────────────────
export function VoletCoche({ a, etape, fermer }: { a: AffectationJour; etape: EtapeJour; fermer: () => void }) {
  const notifier = useNotifier();
  const { envoyer } = useSaisie();
  const { enAttente } = useFileEnvoi();
  const restant = useMemo(() => Math.max(0, Number(etape.qty_planned) - Number(etape.qty_done)), [etape]);
  const [mode, setMode] = useState<'fait' | 'partiel'>('fait');
  const [qte, setQte] = useState('');
  const [avec, setAvec] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [annuler, setAnnuler] = useState<CocheEtape | null>(null);
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [op] = useState(nouvelEnvoi);
  const [opPhoto] = useState(nouvelEnvoi);
  // Étape « photo demandée » (incrément 5) : la coche « faite » propose la photo ; passer outre demande un motif.
  const [photo, setPhoto] = useState<File | null>(null);
  const [sansPhoto, setSansPhoto] = useState('');
  const [photoEnvoyee, setPhotoEnvoyee] = useState(false);
  // Une photo de l'étape déjà prise sur ce téléphone (en attente d'envoi) compte comme prise.
  const photosEnAttente = enAttente.filter((o) => o.type === 'photo' && o.etapeId === etape.id).length;
  // Photos désactivées par la société : l'étape « photo demandée » n'est plus bloquante (le serveur l'envoie déjà à faux).
  const { photos: photosActives } = useReglagesSalaries();
  const photoDemandee = photosActives && etape.photo_required && mode === 'fait' && !annuler && etape.photos === 0 && photosEnAttente === 0 && !photoEnvoyee;
  const collegues = [...new Map(a.coequipiers.filter((p) => p.actif).map((p) => [p.id, p])).values()];
  const dejaFaite = etape.statut === 'fait';
  const attente = cochesEnAttente(enAttente, etape.id);
  const chantier = { chantierId: a.chantier.id, chantierLibelle: libelleChantier(a.chantier) };

  const valider = async () => {
    setErreur(null); setEnvoi(true);
    try {
      if (annuler) {
        const s = await envoyer([{ ...chantier, clientOpId: op, type: 'annulation', etapeId: etape.id, donnees: { cocheId: annuler.id, motif },
          libelle: `Annulation de la coche « ${etape.label} »` }]);
        if (s.etat === 'refusee') { setErreur(s.message ?? 'Annulation refusée.'); return; }
        notifier(s.etat === 'envoyee' ? 'Coche annulée : elle reste visible, barrée, avec votre motif.' : `Annulation enregistrée. ${GARDEE}`);
      } else {
        // La photo d'abord (rattachée à l'étape, envoyée juste avant la coche) : la coche la trouve ensuite côté serveur.
        const avecPhoto = photoDemandee && !!photo;
        const ops = [
          ...(avecPhoto ? [await operationPhoto({ ...chantier, fichier: photo!, rattachement: { etapeId: etape.id }, clientOpId: opPhoto, prealable: true,
            libelle: `Photo de l’étape « ${etape.label} »` })] : []),
          { ...chantier, clientOpId: op, type: 'coche' as const, etapeId: etape.id,
            donnees: { etapeId: etape.id, quantite: mode === 'partiel' ? qte : null, realisePar: avec, note: note || null,
              sansPhotoMotif: photoDemandee && !photo ? sansPhoto : null },
            libelle: `Coche « ${etape.label} »${mode === 'partiel' ? ` — ${qte} ${etape.unit} faits` : ''}` },
        ];
        const s = await envoyer(ops);
        if (avecPhoto && verdictDe(opPhoto)) setPhotoEnvoyee(true);
        if (s.etat === 'refusee') { setErreur(s.message ?? 'Coche refusée.'); return; }
        if (s.etat === 'en_attente') notifier(`${mode === 'partiel' ? 'Partie enregistrée' : 'Étape cochée'}. ${GARDEE}`);
        else notifier(mode === 'partiel' ? 'Partie enregistrée.' : 'Étape cochée.');
        for (const x of [...s.avertissements, ...texteSecondaires(s)].slice(0, 2)) notifier(x, true);
      }
      fermer();
    } catch (e) { setErreur(messageSaisie(e)); } finally { setEnvoi(false); }
  };

  const pret = annuler ? motif.trim().length >= 5
    : mode === 'fait' ? !photoDemandee || !!photo || sansPhoto.trim().length >= 5 : Number(qte.replace(',', '.')) > 0;
  return (
    <Dialogue titre={annuler ? 'Annuler ma coche' : 'Cocher l’étape'} ouvert fermer={fermer}
      pied={<>
        <Bouton onClick={annuler ? () => { setAnnuler(null); setMotif(''); } : fermer}>{annuler ? 'Retour' : 'Fermer'}</Bouton>
        <Bouton variante={annuler ? 'danger' : 'principal'} chargement={envoi} disabled={!pret} onClick={valider}>
          {annuler ? 'Annuler la coche' : 'Valider'}</Bouton>
      </>}>
      <div className="pile jl-volet">
        <div className="jl-etape">
          <strong className="jl-etape-label">{etape.label}</strong>
          <span className="petit secondaire chiffre">Prévu : {quantite(etape.qty_planned, etape.unit)}
            {Number(etape.qty_done) > 0 ? ` — fait : ${quantite(etape.qty_done, etape.unit)}` : ''}</span>
          {etape.detail && <span className="petit secondaire">{etape.detail}</span>}
        </div>
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        {annuler ? (
          <>
            <p className="petit" style={{ margin: 0 }}>{texteCoche(annuler, etape.unit)}. La coche restera visible, barrée, avec votre motif.</p>
            <ChampTexte libelle="Motif (obligatoire)" rows={2} value={motif} onChange={(e) => setMotif(e.target.value)} maxLength={1000}
              aide="Exemple : cochée par erreur, étape à refaire." />
          </>
        ) : (
          <>
            {etape.coches.length > 0 && (
              <ul className="jl-coches" aria-label="Déjà cochée">
                {etape.coches.map((c, i) => (
                  <li key={c.id}>
                    <span className="petit">{i > 0 && c.sorte === 'fait' ? 'Aussi déclarée faite : ' : ''}{texteCoche(c, etape.unit)}{c.hors_poste ? ' (hors de son poste)' : ''}</span>
                    {c.moi && <Bouton compact variante="fantome" onClick={() => setAnnuler(c)}>Annuler ma coche</Bouton>}
                  </li>
                ))}
              </ul>
            )}
            {attente.length > 0 && (
              <ul className="jl-coches" aria-label="Sur ce téléphone, en attente d’envoi">
                {attente.map((o) => (
                  <li key={o.clientOpId}><span className="petit"><CloudUpload size={14} aria-hidden /> Cochée sur ce téléphone à {heureMinute(o.occurredAt)} — en attente d’envoi</span></li>
                ))}
              </ul>
            )}
            {dejaFaite && <p className="petit secondaire" style={{ margin: 0 }}>Cette étape est déjà faite. Cocher de nouveau garde les deux coches.</p>}
            <fieldset className="pl-cases">
              <legend className="petit">Ce qui est fait</legend>
              <div className="jl-modes">
                <label className={`ch-choix ${mode === 'fait' ? 'est-choisi' : ''}`}>
                  <input type="radio" name="mode-coche" checked={mode === 'fait'} onChange={() => setMode('fait')} />Faite</label>
                <label className={`ch-choix ${mode === 'partiel' ? 'est-choisi' : ''}`}>
                  <input type="radio" name="mode-coche" checked={mode === 'partiel'} onChange={() => setMode('partiel')} />Faite en partie</label>
              </div>
            </fieldset>
            {mode === 'partiel' && (
              <Champ libelle={`Quantité faite maintenant (${etape.unit})`} inputMode="decimal" value={qte} onChange={(e) => setQte(e.target.value)}
                aide={restant > 0 ? `Reste ${nombre(restant, 4)} ${etape.unit} sur ${nombre(etape.qty_planned, 4)}.` : undefined} autoFocus />
            )}
            {photoDemandee && (
              <div className="pile jl-photo-demandee">
                <ChampPhoto libelle="Photo demandée pour cette étape" photo={photo} changer={setPhoto} />
                {!photo && (
                  <ChampTexte libelle="Pas de photo ? Indiquez pourquoi" rows={2} value={sansPhoto} onChange={(e) => setSansPhoto(e.target.value)} maxLength={1000}
                    aide="5 caractères au moins ; le motif reste sur la coche." />
                )}
              </div>
            )}
            {etape.photo_required && etape.photos > 0 && mode === 'fait' && (
              <p className="petit secondaire" style={{ margin: 0 }}>Photo demandée : {etape.photos > 1 ? `${etape.photos} photos déjà prises` : 'une photo déjà prise'} pour cette étape.</p>
            )}
            {collegues.length > 0 && (
              <fieldset className="pl-cases">
                <legend className="petit">Réalisé avec (facultatif)</legend>
                <div className="pl-personnes-choix">
                  {collegues.map((p) => (
                    <label key={p.id} className={`pl-choix ${avec.includes(p.id) ? 'est-choisi' : ''}`}>
                      <input type="checkbox" checked={avec.includes(p.id)} onChange={(e) => setAvec(e.target.checked ? [...avec, p.id] : avec.filter((x) => x !== p.id))} />
                      {p.nom}</label>
                  ))}
                </div>
              </fieldset>
            )}
            <ChampTexte libelle="Note (facultatif)" rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} />
          </>
        )}
      </div>
    </Dialogue>
  );
}

// ─── Consigne de relève ──────────────────────────────────────────────────────────────────────────────
export function DialogueReleve({ a, fermer }: { a: AffectationJour; fermer: () => void }) {
  const notifier = useNotifier();
  const { envoyer } = useSaisie();
  const [texte, setTexte] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [op] = useState(nouvelEnvoi);
  const valider = async () => {
    setErreur(null); setEnvoi(true);
    try {
      const s = await envoyer([{ clientOpId: op, type: 'releve', chantierId: a.chantier.id, chantierLibelle: libelleChantier(a.chantier),
        donnees: { texte, posteId: a.poste?.id ?? null }, libelle: `Consigne de relève${a.poste ? ` (${a.poste.label})` : ''} : ${texte.slice(0, 80)}` }]);
      if (s.etat === 'refusee') { setErreur(s.message ?? 'Consigne refusée.'); return; }
      notifier(s.etat === 'envoyee' ? 'Consigne laissée : elle s’affiche en tête du poste pour l’équipe suivante.' : `Consigne enregistrée. ${GARDEE}`);
      fermer();
    } catch (e) { setErreur(messageErreur(e)); } finally { setEnvoi(false); }
  };
  return (
    <Dialogue titre="Consigne de relève" ouvert fermer={fermer}
      pied={<><Bouton onClick={fermer}>Fermer</Bouton><Bouton variante="principal" chargement={envoi} disabled={!texte.trim()} onClick={valider}>Valider</Bouton></>}>
      <div className="pile">
        <p className="petit secondaire" style={{ margin: 0 }}>Pour {a.poste ? <>le poste « {a.poste.label} »</> : 'tout le chantier'} : ce qui reste, où vous vous êtes arrêté, ce qu’il faut savoir.</p>
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <ChampTexte libelle="Consigne" rows={4} value={texte} onChange={(e) => setTexte(e.target.value)} maxLength={2000} autoFocus />
      </div>
    </Dialogue>
  );
}

// ─── Temps passé (déclaré, puis corrigeable ; ce n'est pas un pointage légal) ──────────────────────────────
export function DialogueTemps({ a, fermer }: { a: AffectationJour; fermer: () => void }) {
  const notifier = useNotifier();
  const { envoyer } = useSaisie();
  // Proposition visible et modifiable : le temps déjà déclaré, sinon les heures prévues de l'affectation, sinon vide.
  const propose = a.mon_temps?.heures ?? a.heures ?? '';
  const [heures, setHeures] = useState(propose ? String(Number(propose)).replace('.', ',') : '');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [op] = useState(nouvelEnvoi);
  const valider = async () => {
    setErreur(null); setEnvoi(true);
    try {
      const s = await envoyer([{ clientOpId: op, type: 'temps', chantierId: a.chantier.id, chantierLibelle: libelleChantier(a.chantier),
        donnees: { jour: a.jour, heures }, libelle: `Temps passé le ${a.jour.slice(8, 10)}/${a.jour.slice(5, 7)} : ${heures} h` }]);
      if (s.etat === 'refusee') { setErreur(s.message ?? 'Temps refusé.'); return; }
      const inchange = s.verdict?.statut === 'sans_effet';
      notifier(s.etat === 'en_attente' ? `Temps enregistré. ${GARDEE}`
        : inchange ? 'Temps inchangé.' : a.mon_temps ? 'Temps corrigé (la déclaration précédente reste dans l’historique).' : 'Temps déclaré.');
      fermer();
    } catch (e) { setErreur(messageErreur(e)); } finally { setEnvoi(false); }
  };
  return (
    <Dialogue titre="Mon temps passé aujourd’hui" ouvert fermer={fermer}
      pied={<><Bouton onClick={fermer}>Fermer</Bouton><Bouton variante="principal" chargement={envoi} disabled={!heures.trim()} onClick={valider}>Valider</Bouton></>}>
      <div className="pile">
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <Champ libelle="Heures passées sur ce chantier" inputMode="decimal" value={heures} onChange={(e) => setHeures(e.target.value)} autoFocus
          aide={a.mon_temps ? `Déjà déclaré : ${nombre(a.mon_temps.heures, 2)} h. Corriger garde l’historique.`
            : a.heures ? `Proposé d’après votre affectation (${nombre(a.heures, 2)} h) : modifiez si besoin.` : 'Exemple : 7 ou 6,5.'} />
        <p className="tres-petit discret" style={{ margin: 0 }}>Sert au suivi du chantier (prévu comparé au passé). Ce n’est pas le décompte légal de votre temps de travail.</p>
      </div>
    </Dialogue>
  );
}
